import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TextAreaField } from "@/components/common/text-field";
import ScrollModal from "@/components/common/scroll-modal";
import {
  Button,
  Calendar,
  Icon,
  useToast,
  Switch,
} from "@elaniintc/goes-design-system";
import { DialogTitle } from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useArchiveProcess } from "@/queries/process";

interface ArchiveProcessModalProps {
  isOpen: boolean;
  processId: string;
  onClose: () => void;
}

const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

const archiveSchema = z
  .object({
    reason: z
      .string()
      .refine((val) => val.length <= 120, {
        error: "Máximo 120 caracteres",
      })
      .optional()
      .or(z.literal("")),
    scheduleArchive: z.boolean(),
    startDate: z.string().optional(),
    startTime: z.string().optional(),
    startPeriod: z.enum(["AM", "PM"]).optional(),
    endDate: z.string().optional(),
    endTime: z.string().optional(),
    endPeriod: z.enum(["AM", "PM"]).optional(),
  })
  .refine(
    (data) => {
      if (data.scheduleArchive) {
        return (
          !!data.startDate &&
          !!data.startTime &&
          !!data.endDate &&
          !!data.endTime
        );
      }
      return true;
    },
    {
      path: ["scheduleArchive"],
      error:
        "Todos los campos de fecha son requeridos cuando se programa la archivación",
    },
  )
  .refine(
    (data) => {
      if (data.scheduleArchive && data.startTime) {
        return timeRegex.test(data.startTime);
      }
      return true;
    },
    {
      path: ["startTime"],
      error: "Formato de hora inválido. Usa HH:MM (00:00 - 23:59)",
    },
  )
  .refine(
    (data) => {
      if (data.scheduleArchive && data.endTime) {
        return timeRegex.test(data.endTime);
      }
      return true;
    },
    {
      path: ["endTime"],
      error: "Formato de hora inválido. Usa HH:MM (00:00 - 23:59)",
    },
  )
  .refine(
    (data) => {
      if (data.scheduleArchive && data.startDate && data.endDate) {
        const [startDay, startMonth, startYear] = data.startDate.split("-");
        const [endDay, endMonth, endYear] = data.endDate.split("-");
        const start = new Date(
          parseInt(startYear),
          parseInt(startMonth) - 1,
          parseInt(startDay),
        );
        const end = new Date(
          parseInt(endYear),
          parseInt(endMonth) - 1,
          parseInt(endDay),
        );
        return end >= start;
      }
      return true;
    },
    {
      path: ["endDate"],
      error: "La fecha de fin debe ser posterior o igual a la fecha de inicio",
    },
  )
  .refine(
    (data) => {
      if (
        data.scheduleArchive &&
        data.startDate &&
        data.endDate &&
        data.startTime &&
        data.endTime
      ) {
        if (data.startDate === data.endDate) {
          const [startHour, startMinute] = data.startTime.split(":");
          const [endHour, endMinute] = data.endTime.split(":");

          const startTotalMinutes =
            parseInt(startHour) * 60 + parseInt(startMinute);
          const endTotalMinutes = parseInt(endHour) * 60 + parseInt(endMinute);

          let adjustedStartMinutes = startTotalMinutes;
          let adjustedEndMinutes = endTotalMinutes;

          if (data.startPeriod === "PM" && parseInt(startHour) !== 12) {
            adjustedStartMinutes += 12 * 60;
          } else if (data.startPeriod === "AM" && parseInt(startHour) === 12) {
            adjustedStartMinutes -= 12 * 60;
          }

          if (data.endPeriod === "PM" && parseInt(endHour) !== 12) {
            adjustedEndMinutes += 12 * 60;
          } else if (data.endPeriod === "AM" && parseInt(endHour) === 12) {
            adjustedEndMinutes -= 12 * 60;
          }

          return adjustedEndMinutes > adjustedStartMinutes;
        }
        return true;
      }
      return true;
    },
    {
      path: ["endTime"],
      error:
        "La hora de fin debe ser posterior a la hora de inicio en el mismo día",
    },
  );

type ArchiveFormData = z.infer<typeof archiveSchema>;

const ArchiveProcessModal = ({
  isOpen,
  processId,
  onClose,
}: ArchiveProcessModalProps) => {
  const { showToast } = useToast();
  const [openStartCalendar, setOpenStartCalendar] = useState(false);
  const [openEndCalendar, setOpenEndCalendar] = useState(false);
  const { mutateAsync, isPending } = useArchiveProcess();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ArchiveFormData>({
    resolver: zodResolver(archiveSchema),
    mode: "onChange",
    defaultValues: {
      reason: "",
      scheduleArchive: false,
      startDate: "",
      startTime: "",
      startPeriod: "AM",
      endDate: "",
      endTime: "",
      endPeriod: "AM",
    },
  });

  const [scheduleArchive, reasonValue, startPeriod, endPeriod] = watch([
    "scheduleArchive",
    "reason",
    "startPeriod",
    "endPeriod",
  ]);

  const convertToDateTime = (
    dateStr: string,
    timeStr: string,
    period: "AM" | "PM",
  ): string => {
    const [day, month, year] = dateStr.split("-");
    const [hours, minutes] = timeStr.split(":");

    let hour = parseInt(hours);

    if (period === "PM" && hour !== 12) {
      hour += 12;
    } else if (period === "AM" && hour === 12) {
      hour = 0;
    }

    // Create Date object and convert to ISO 8601 format
    const date = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      hour,
      parseInt(minutes),
      0,
      0,
    );

    return date.toISOString();
  };

  const handleFormSubmit = async (data: ArchiveFormData) => {
    try {
      await mutateAsync({
        id: processId,
        payload: {
          status: "INACTIVE",
          reason: data.reason,
          inactive_from: data.scheduleArchive
            ? convertToDateTime(
                data.startDate!,
                data.startTime!,
                data.startPeriod!,
              )
            : undefined,
          inactive_to: data.scheduleArchive
            ? convertToDateTime(data.endDate!, data.endTime!, data.endPeriod!)
            : undefined,
        },
      });

      showToast({
        title: "Proceso archivado",
        description: "El proceso ha sido archivado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
      onClose();
    } catch {
      showToast({
        title: "Error al archivar",
        description: "No se pudo archivar el proceso. Intenta nuevamente.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDate = (dateString: string): Date | undefined => {
    if (!dateString) return undefined;
    const [day, month, year] = dateString.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  const handleDateSelect = (
    date: Date | undefined,
    field: "startDate" | "endDate",
    setOpen: (open: boolean) => void,
  ) => {
    if (date) {
      setValue(field, formatDate(date), { shouldValidate: true });
      setTimeout(() => {
        trigger(field);
      }, 0);
    }
    setOpen(false);
  };

  const handleTimeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "startTime" | "endTime",
  ) => {
    let value = e.target.value;

    value = value.replace(/[^0-9:]/g, "");

    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    if (value.length === 2 && !value.includes(":")) {
      value = value + ":";
    }

    const parts = value.split(":");
    if (parts[0] && parseInt(parts[0]) > 23) {
      parts[0] = "23";
      value = parts.join(":");
    }

    if (parts[1] && parseInt(parts[1]) > 59) {
      parts[1] = "59";
      value = parts.join(":");
    }

    setValue(field, value);

    if (timeRegex.test(value)) {
      setTimeout(() => {
        trigger(field);
      }, 0);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 120) {
      setValue("reason", value);
      trigger("reason");
    }
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] h-full flex flex-col gap-0"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle>Archivar proceso</DialogTitle>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2 py-3 px-8 rounded-b-lg">
          <Button
            type="button"
            variant="tertiary"
            className="w-22.25 h-11 font-semibold border"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="archive-process-form"
            className="w-[89px] h-[44px] font-semibold"
            disabled={isSubmitting || !isValid || isPending}
          >
            Archivar
          </Button>
        </div>
      }
    >
      <form
        id="archive-process-form"
        onSubmit={handleSubmit(handleFormSubmit)}
        className="p-modal-md flex flex-col gap-4"
      >
        <div className="w-full flex justify-between mb-[-14px]">
          <label className="text-md text-body-loud font-medium">
            Motivo de la archivación
          </label>
          <p className="text-sm text-body-quiet">Opcional</p>
        </div>
        <div className="flex flex-col">
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextAreaField
                label=""
                placeholder="Escribe el motivo de la archivación..."
                maxLength={120}
                className="min-h-[88px]"
                value={field.value || ""}
                onChange={handleReasonChange}
                onBlur={field.onBlur}
                error={errors.reason?.message || ""}
              />
            )}
          />
          <p className="text-xs text-body-quiet text-right mt-1">
            Caracteres: {reasonValue?.length || 0}/120
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-md text-body-loud font-medium">
            Programar fecha de archivado<span className="text-danger">*</span>
          </label>
          <div className="flex items-center gap-2">
            <Controller
              name="scheduleArchive"
              control={control}
              render={({ field }) => (
                <Switch
                  id="scheduleArchive"
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <span className="text-sm text-body">
              {scheduleArchive ? "Sí" : "No, archivar inmediatamente"}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium text-body-loud text-sm leading-2xl">
            Fecha y hora de inicio<span className="text-danger">*</span>
          </label>
          <div
            className={`flex items-center border rounded-lg h-[44px] bg-neutral-object-accent-silent ${
              !scheduleArchive
                ? "border-neutral-silent bg-neutral-ghost opacity-60"
                : "border-neutral-silent"
            }`}
          >
            <div className="flex-1 border-r border-neutral-silent">
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <Popover
                    open={openStartCalendar}
                    onOpenChange={setOpenStartCalendar}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`w-full flex flex-start items-center gap-2 px-1 bg-transparent text-left ${
                          !scheduleArchive
                            ? "cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        disabled={!scheduleArchive}
                      >
                        <Icon
                          type="far"
                          name="calendar"
                          className={`ml-1 mr-1 text-body-quiet`}
                          size="lg"
                        />
                        <span className="font-normal mt-1 text-body-quiet text-md">
                          {field.value || "DD-MM-YYYY"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0 bg-neutral-object"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={parseDate(field.value as string)}
                        captionLayout="dropdown"
                        className="bg-transparent"
                        onSelect={(date) =>
                          handleDateSelect(
                            date,
                            "startDate",
                            setOpenStartCalendar,
                          )
                        }
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
            </div>

            <div className="flex items-center gap-1 px-3">
              <Icon type="far" name="clock" className="text-body-quiet" />
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <input
                    type="text"
                    placeholder="00:00"
                    value={field.value || ""}
                    onChange={(e) => handleTimeInput(e, "startTime")}
                    onBlur={field.onBlur}
                    disabled={!scheduleArchive}
                    maxLength={5}
                    className="w-16 bg-transparent outline-none text-body-quiet placeholder:text-body-quiet text-md mt-[2px]"
                  />
                )}
              />
            </div>

            <div className="pr-2">
              <select
                value={startPeriod || "AM"}
                onChange={(e) => {
                  setValue("startPeriod", e.target.value as "AM" | "PM");
                  setTimeout(() => {
                    trigger("startPeriod");
                  }, 0);
                }}
                disabled={!scheduleArchive}
                className="appearance-none bg-neutral-object-accent-silent border-neutral-accent outline-none rounded-l text-body-quiet text-sm cursor-pointer px-2 py-1"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
          {errors.startTime && (
            <p className="text-xs text-error">{errors.startTime.message}</p>
          )}
          {errors.startDate && (
            <p className="text-xs text-error">{errors.startDate.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium text-body-loud text-sm leading-2xl">
            Fecha y hora de fin<span className="text-danger">*</span>
          </label>
          <div
            className={`flex items-center border rounded-lg h-[44px] bg-neutral-object-accent-silent ${
              !scheduleArchive
                ? "border-neutral-silent bg-neutral-ghost opacity-60"
                : "border-neutral-silent"
            }`}
          >
            <div className="flex-1 border-r border-neutral-silent">
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <Popover
                    open={openEndCalendar}
                    onOpenChange={setOpenEndCalendar}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`w-full flex items-center gap-2 px-1 py-1 bg-transparent text-left ${
                          !scheduleArchive
                            ? "cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        disabled={!scheduleArchive}
                      >
                        <Icon
                          type="far"
                          name="calendar"
                          className={`ml-1 mr-1 text-body-quiet`}
                          size="lg"
                        />
                        <span className="font-normal mt-1 text-body-quiet text-md">
                          {field.value || "DD-MM-YYYY"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0 bg-neutral-object"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={parseDate(field.value as string)}
                        captionLayout="dropdown"
                        className="bg-transparent"
                        onSelect={(date) =>
                          handleDateSelect(date, "endDate", setOpenEndCalendar)
                        }
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
            </div>

            <div className="flex items-center gap-1 px-3">
              <Icon type="far" name="clock" className="text-body-quiet" />
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <input
                    type="text"
                    placeholder="00:00"
                    value={field.value || ""}
                    onChange={(e) => handleTimeInput(e, "endTime")}
                    onBlur={field.onBlur}
                    disabled={!scheduleArchive}
                    maxLength={5}
                    className="w-16 bg-transparent outline-none text-body-quiet placeholder:text-body-quiet text-md mt-[2px]"
                  />
                )}
              />
            </div>

            <div className="pr-2">
              <select
                value={endPeriod || "AM"}
                onChange={(e) => {
                  setValue("endPeriod", e.target.value as "AM" | "PM");
                  setTimeout(() => {
                    trigger("endPeriod");
                  }, 0);
                }}
                disabled={!scheduleArchive}
                className="appearance-none bg-neutral-object-accent-silent border-neutral-accent outline-none rounded-l text-body-quiet text-sm cursor-pointer px-2 py-1"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
          {errors.endTime && (
            <p className="text-xs text-error">{errors.endTime.message}</p>
          )}
          {errors.endDate && (
            <p className="text-xs text-error">{errors.endDate.message}</p>
          )}
        </div>
      </form>
    </ScrollModal>
  );
};

export default ArchiveProcessModal;
